import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoadPolicyDataComponent } from './component/load-policy-data/load-policy-data.component';
import { AgentAppGuard } from './guards/agent-app.guard';
import { DirectAppGuard } from './guards/direct-app.guard';
import { LoadAppGuard } from './guards/load-app.guard';
import { SuccessGuard } from './guards/success.guard';
import { AgentSuccessComponent } from './page/agent-success/agent-success.component';
import { AuthenticationComponent } from './page/authentication/authentication.component';
import { ConclusionAgentComponent } from './page/conclusion-agent/conclusion-agent.component';
import { PageConclusionRemoteComponent } from './page/conclusion-remote/conclusion-remote.component';
import { ConclusionRequestComponent } from './page/conclusion-request/conclusion-request.component';
import { ConclusionComponent } from './page/conclusion/conclusion.component';
import { PageErrorComponent } from './page/error/error.component';
import { PageLoaderComponent } from './page/loader/loader.component';
import { PageOfferComponent } from './page/offer/offer.component';
import { PaymentStatusComponent } from './page/payment-status/payment-status.component';
import { PaymentComponent } from './page/payment/payment.component';
import { RemoteIdConsentsComponent } from './page/remote-id-consents/remote-id-consents.component';
import { RemoteIdOtpComponent } from './page/remote-id-otp/remote-id-otp.component';
import { RemoteIdComponent } from './page/remote-id/remote-id.component';
import { Step1Component } from './page/step1/step1.component';
import { Step2Component } from './page/step2/step2.component';
import { Step4Component } from './page/step4/step4.component';
import { Step5Component } from './page/step5/step5.component';
import { Step6Component } from './page/step6/step6.component';
import { SuccessComponent } from './page/success/success.component';
import isAppAgentOrTravelAgency from './utils/is-agent-or-travel-agency.util';

const paymentGuards = [SuccessGuard];
const remoteGuards = [SuccessGuard];
const defaultGuards = [SuccessGuard, LoadAppGuard];
const directGuards = [...defaultGuards, DirectAppGuard];
const agentGuards = [...defaultGuards, AgentAppGuard];

const SHARED_ROUTES: Routes = [
  {
    path: '',
    component: PageLoaderComponent,
  },
  {
    path: 'policy/:policyNumber',
    component: LoadPolicyDataComponent,
    canActivate: defaultGuards,
  },
  {
    path: 'lead/:leadId',
    component: LoadPolicyDataComponent,
    canActivate: defaultGuards,
  },
  {
    path: 'remote/:id',
    component: RemoteIdComponent,
    canActivate: remoteGuards,
  },
  {
    path: 'remote/:id/otp',
    component: RemoteIdOtpComponent,
    canActivate: remoteGuards,
  },
  {
    path: 'remote/:id/consents',
    component: RemoteIdConsentsComponent,
    canActivate: remoteGuards,
  },
  {
    path: 'remote/:id/payment',
    component: PaymentComponent,
    canActivate: paymentGuards,
  },
  {
    path: 'remote/:id/payment/:transactionGUID/status',
    component: PaymentStatusComponent,
    canActivate: paymentGuards,
  },
  {
    path: 'remote/:id/success',
    component: RemoteIdOtpComponent,
    canActivate: remoteGuards,
  },
  {
    path: 'error/auth',
    component: AuthenticationComponent,
  },
  {
    path: '**',
    component: PageErrorComponent,
  },
];

const DIRECT_ROUTES: Routes = [
  {
    path: 'page/step1',
    component: Step1Component,
    canActivate: defaultGuards,
  },
  {
    path: 'page/step2',
    component: Step2Component,
    canActivate: directGuards,
  },
  {
    path: 'page/step3',
    component: PageOfferComponent,
    canActivate: directGuards,
  },
  {
    path: 'page/step4',
    component: Step4Component,
    canActivate: directGuards,
  },
  {
    path: 'page/step5',
    component: Step5Component,
    canActivate: directGuards,
  },
  {
    path: 'page/step6',
    component: Step6Component,
    canActivate: directGuards,
  },
  {
    path: 'page/payment',
    component: PaymentComponent,
    canActivate: paymentGuards,
  },
  {
    path: 'page/payment/:transactionGUID/status',
    component: PaymentStatusComponent,
    canActivate: paymentGuards,
  },
  {
    path: 'page/success',
    component: SuccessComponent,
    canActivate: directGuards,
  },
  ...SHARED_ROUTES,
];

const AGENT_ROUTES: Routes = [
  {
    path: 'page/step1',
    component: Step1Component,
    canActivate: defaultGuards,
  },
  {
    path: 'page/step2',
    component: Step2Component,
    canActivate: agentGuards,
  },
  {
    path: 'page/step3',
    component: PageOfferComponent,
    canActivate: agentGuards,
  },
  {
    path: 'page/step4',
    component: Step4Component,
    canActivate: agentGuards,
  },
  {
    path: 'page/step5',
    component: Step5Component,
    canActivate: agentGuards,
  },
  {
    path: 'page/step6',
    component: Step6Component,
    canActivate: agentGuards,
  },
  {
    path: 'page/conclusion',
    component: ConclusionComponent,
    canActivate: agentGuards,
  },
  {
    path: 'page/conclusion/agent',
    component: ConclusionAgentComponent,
    canActivate: agentGuards,
  },
  {
    path: 'page/conclusion/remote',
    component: PageConclusionRemoteComponent,
    canActivate: agentGuards,
    pathMatch: 'full',
  },
  {
    path: 'page/conclusion/request',
    component: ConclusionRequestComponent,
    canActivate: agentGuards,
  },
  {
    path: 'page/success',
    component: AgentSuccessComponent,
    canActivate: [SuccessGuard],
  },
  ...SHARED_ROUTES,
];

const routes: Routes = isAppAgentOrTravelAgency() ? AGENT_ROUTES : DIRECT_ROUTES;

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true, scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
